// src/services/pdf/generateContrato.js
// Genera el "Contrato Actuacion Musical" calcando la plantilla cerrada
// docs/PLANTILLA_Contrato Los Toldos_Nombre y Nombre_Fecha.docx (y su version
// parametrizada docs/PLANTILLA_Contrato_Los_Toldos.md).
//
// El texto de las clausulas es literal y la maqueta esta medida sobre el
// propio .docx: cualquier cambio de redaccion o de formato debe hacerse en la
// plantilla y aqui a la vez.

import { jsPDF } from "jspdf";
import { preloadLogos, drawContractLogo, drawFooter } from "./pdfBranding.js";
import {
    IVA_RATE,
    LEGAL_REP,
    BANK_NAME,
    CONTRACT_EMAIL,
    formatAmount,
    formatDate,
    todayFormatted,
    safeFileName,
} from "./pdfConfig.js";

// =========================
// Geometria calcada del .docx
// =========================
// Los valores entre parentesis son los del OOXML del que salen. Word mide en
// twips (1/1440") y medios puntos; aqui todo va en mm porque es la unidad por
// defecto de jsPDF.
const PAGE_H = 297;
const MARGIN_X = 30;            // w:pgMar left/right 1701 twips
const MARGIN_BOTTOM = 25;       // w:pgMar bottom 1417 twips
const CONTENT_TOP = 30;         // w:pgMar top 1417 + hueco del logo de cabecera

const FS = 8;                   // w:sz 16 (medios puntos) en todas las runs
const LINE = 3.3;               // w:line 278 "auto" => 1,16 x 8pt
const PARA_GAP = 2.8;           // w:spacing after 160 twips

// Tabla de clausulas: 2 columnas iguales, rejilla negra fina, sin relleno.
const TABLE_X = MARGIN_X - 3;   // w:tblInd -168 twips
const COL_W = 79.05;            // w:gridCol 4482 twips
const CELL_PAD_X = 2;           // w:tblCellMar left/right 115 twips
const CELL_PAD_Y = 1.2;
const BORDER = 0.176;           // w:sz 4 => 0,5pt

// Tabla de firmas: 180 mm centrada y sin bordes visibles (los del Word son
// blancos sobre blanco).
const SIGN_X = 15;
const SIGN_COL_W = 90;

// Reparto por defecto si el evento no dice otra cosa: 30 % de reserva.
export const DEFAULT_RESERVA_PCT = 30;

/** Deja el porcentaje en un entero utilizable: fuera de 1..99 no hay contrato. */
export function normalizeReservaPct(pct) {
    const n = Math.round(Number(pct));
    if (!Number.isFinite(n) || n < 1 || n > 99) return DEFAULT_RESERVA_PCT;
    return n;
}

/**
 * Reparte la base imponible en reserva y resto segun el porcentaje pactado.
 *
 * El resto se calcula por diferencia en vez de aplicar su porcentaje por
 * separado: con bases como 12.345,67 los dos redondeos independientes no suman
 * la base y el contrato mostraria un descuadre de centimos.
 *
 * @param {number} base       - Base imponible (honorarios sin IVA)
 * @param {number} reservaPct - Porcentaje de reserva, 1..99. Por defecto 30.
 */
export function splitHonorarios(base, reservaPct = DEFAULT_RESERVA_PCT) {
    const pct = normalizeReservaPct(reservaPct);
    const total = round2(base);
    const reservaBase = round2(total * pct / 100);
    const restoBase = round2(total - reservaBase);

    const iva = (n) => round2(n * IVA_RATE);

    return {
        reservaPct: pct,
        restoPct: 100 - pct,
        base: total,
        iva: iva(total),
        total: round2(total + iva(total)),
        reservaBase,
        reservaIva: iva(reservaBase),
        reservaTotal: round2(reservaBase + iva(reservaBase)),
        restoBase,
        restoIva: iva(restoBase),
        restoTotal: round2(restoBase + iva(restoBase)),
    };
}

function round2(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Genera el contrato de actuacion musical.
 *
 * @param {object} event        - Datos del evento
 * @param {object} contact      - Contacto principal del evento
 * @param {object} companyData  - Datos de empresa (buildCompanyData)
 * @param {string} docNumber    - Numero correlativo ya consumido
 * @param {object} contractData - Datos capturados en el preflight:
 *        { signer1, signer2, domicilio, contacto, lugarFirma, honorariosBase }
 *        donde signerN = { treatment, name, nif }
 */
export async function generateContratoPDF(
    event,
    contact = {},
    companyData = {},
    docNumber = "",
    contractData = {}
) {
    if (!event) {
        console.error("No event data for Contrato");
        return;
    }

    await preloadLogos();
    const doc = new jsPDF({ compress: true });
    const cur = makeCursor(doc);

    // ── Datos, con el preflight como fuente principal y el contacto de reserva
    const s1 = contractData.signer1 || {};
    const s2 = contractData.signer2 || {};

    const signer1 = {
        treatment: s1.treatment || "D./Dña.",
        name: s1.name || [contact.name, contact.surname].filter(Boolean).join(" ") || "________________",
        nif: s1.nif || contact.nif || "____________",
    };
    const signer2 = {
        treatment: s2.treatment || "D./Dña.",
        name: s2.name || "________________",
        nif: s2.nif || "____________",
    };

    const domicilio = contractData.domicilio
        || [contact.fiscal_address, contact.fiscal_zip, contact.fiscal_city].filter(Boolean).join(", ")
        || "________________";
    const contacto = contractData.contacto || contact.email || contact.phone || "________________";
    const lugarFirma = contractData.lugarFirma || companyData.city || "Bilbao";

    // El preflight propone el nombre del venue ("Finca Las Margas, Segovia");
    // events.location es el respaldo porque suele traer indicaciones sueltas.
    const lugarEvento = contractData.lugarEvento
        || event.location || event.city || event.region || "________________";

    const honorariosBase = contractData.honorariosBase != null
        ? Number(contractData.honorariosBase)
        : Number(event.amount || 0);
    const money = splitHonorarios(honorariosBase, contractData.reservaPct);

    const companyName = companyData.name || "LOS TOLDOS ENTERTAINMENT, S.L.";
    const companyCif = companyData.cif || "";
    // El Word cita el domicilio social completo, con provincia.
    const companyAddress = [companyData.address, companyData.city, companyData.province]
        .filter(Boolean).join(", ");
    const iban = companyData.iban || "";
    const contactEmail = companyData.email || CONTRACT_EMAIL;

    // === TITULO ===
    drawContractLogo(doc);

    cur.centered("**CONTRATO ACTUACIÓN MUSICAL**");
    cur.y += PARA_GAP;
    cur.paragraph(`En ${esc(lugarFirma)}, a ${todayFormatted()}.`, { justify: false });

    // === COMPARECEN ===
    cur.centered("**COMPARECEN**");
    cur.paragraph(
        `De una parte, **Don ${esc(LEGAL_REP.name)}**, con DNI ${esc(LEGAL_REP.nif)}, en nombre y `
        + `representación de la mercantil **${esc(companyName)}**, con CIF ${esc(companyCif)} y `
        + `domicilio sito en ${esc(companyAddress)} (en adelante, “**Los TOLDOS**”).`
    );
    cur.paragraph("Y,");
    cur.paragraph(
        `De otra parte, **${esc(signer1.treatment)} ${esc(signer1.name)}** y `
        + `**${esc(signer2.treatment)} ${esc(signer2.name)}** (en adelante, conjuntamente, `
        + `“**Los NOVIOS**”), con DNI ${esc(signer1.nif)} y ${esc(signer2.nif)}, respectivamente, y `
        + `domicilio sito a efectos de notificaciones en ${esc(domicilio)}.`
    );
    cur.y += PARA_GAP;

    // === CLAUSULAS ===
    drawClausulasHeader(cur);
    for (const [label, paras] of buildClausulas({ event, lugarEvento, money, iban, contactEmail, contacto })) {
        drawClausulaRow(cur, label, paras);
    }
    cur.y += PARA_GAP * 2;

    // === CIERRE Y FIRMAS ===
    cur.ensure(45);
    cur.paragraph(
        "Y, para ser cumplido de buena fe, reconociéndose ambas partes plena capacidad, firman el "
        + "presente contrato por duplicado, junto con sus anexos, a un sólo efecto en el lugar y fecha "
        + "ut supra indicados."
    );
    cur.y += PARA_GAP;
    drawFirmas(cur, { companyName, signer1, signer2 });

    // === ANEXO I ===
    cur.newPage();
    cur.centered("**ANEXO I**");
    cur.centered("**Requisitos técnicos necesarios para el desarrollo del Evento**");
    cur.y += PARA_GAP;

    for (const item of ANEXO_I) cur.bullet(item);
    for (const item of ANEXO_I_RESPONSABILIDADES) cur.bullet(item, { indent: 6, marker: "-" });
    cur.y += 1;
    for (const item of ANEXO_I_CONT) cur.bullet(item);

    drawFooter(doc, { docNumber });

    const safeName = safeFileName(event.client_name_legacy);
    doc.save(`Contrato_${safeName}${docNumber ? "_" + docNumber : ""}.pdf`);
}

// =========================
// Cuerpo de clausulas (texto literal de la plantilla)
// =========================
// Cada entrada es [rotulo, [parrafos]]. Los ** marcan las runs en negrita del
// .docx y cada elemento del array es un <w:p> de la celda.
function buildClausulas({ event, lugarEvento, money, iban, contactEmail, contacto }) {
    return [
        ["**Objeto del Contrato**", [
            "El presente contrato tiene como objeto regular la actuación musical, su precio y demás "
            + "condiciones que desarrollará el grupo musical Los TOLDOS (en adelante, el “**Evento**”) a "
            + "solicitud de Los Novios.",
        ]],

        ["**Fecha del Evento**", [formatDate(event.date)]],

        ["**Lugar del Evento**", [esc(lugarEvento)]],

        ["**Honorarios del Evento**", [
            `${formatAmount(money.base)} EUROS + IVA 21% (${formatAmount(money.iva)} EUROS), `
            + `lo que asciende a un total de ${formatAmount(money.total)} EUROS.`,
        ]],

        ["**Forma de pago de los honorarios del Evento**", [
            "Los honorarios del Evento se abonarán de la siguiente forma:",

            `El ${money.reservaPct}% a la firma del presente contrato, en concepto de reserva de la `
            + `fecha del Evento: ${formatAmount(money.reservaBase)} EUROS + IVA 21%, es decir, `
            + `${formatAmount(money.reservaTotal)} EUROS.`,

            `El restante ${money.restoPct}% el lunes previo a la celebración del Evento: `
            + `${formatAmount(money.restoBase)} EUROS + IVA 21%, es decir, `
            + `${formatAmount(money.restoTotal)} EUROS.`,

            "Ambos abonos se efectuarán a través de transferencia bancaria al número de cuenta "
            + "titularidad de LOS TOLDOS ENTERTAINMENT SL:",

            esc(iban),
            BANK_NAME,

            "En caso de impago de cualquiera de los abonos antedichos en las fechas indicadas, Los "
            + "TOLDOS tendrán derecho a cancelar el Evento sin necesidad de reembolsar las cantidades "
            + "hasta el momento percibidas.",
        ]],

        ["**Política de cancelación del Evento**", [
            "En caso de cancelación del Evento por parte de Los NOVIOS, se procederá por Los TOLDOS al "
            + `reembolso de la cantidad abonada en concepto de reserva (${money.reservaPct}% de los `
            + "honorarios), siempre "
            + "y cuando la cancelación fuera comunicada por escrito con una antelación mínima de 8 meses "
            + "a la fecha de celebración del Evento pactada en el presente contrato. En caso de no "
            + "respetarse dicho periodo de preaviso mínimo de 8 meses, Los NOVIOS no tendrán derecho al "
            + "reembolso de la cantidad abonada.",

            "En caso de que a instancias de Los NOVIOS se propusiera un cambio de fecha del Evento, y "
            + "siempre y cuando éste se comunique por escrito a Los TOLDOS con una antelación superior a "
            + "6 meses respecto a la fecha pactada inicialmente en el presente contrato, ambas partes se "
            + "comprometen de buena fe a poner sus mejores esfuerzos en la búsqueda de una nueva fecha "
            + "disponible para la celebración del Evento en un nuevo periodo de un año a contar desde la "
            + "solicitud de cambio de fecha. En caso de que la agenda previa de Los TOLDOS no fuera "
            + "compatible con la(s) nueva(s) fecha(s) propuesta(s) por Los NOVIOS, el importe abonado de "
            + "reserva no será reembolsado, sin perjuicio de que Los TOLDOS se comprometen, si así lo "
            + "desean Los NOVIOS, a recomendar y gestionar otro grupo musical sustituto a través de su "
            + "mediación en la obtención del mejor acuerdo posible con el grupo musical alternativo.",

            "En caso de que la cancelación o cambio de fecha del Evento vengan motivados exclusivamente "
            + "por causas extraordinarias de fuerza mayor (no imputables a ninguna de las partes, tales "
            + "como guerras, pandemias, desastres naturales o similares) que impidan su celebración en "
            + "los términos pactados en el presente contrato, ambas partes se comprometen de buena fe a "
            + "poner sus mejores esfuerzos en la búsqueda de una nueva fecha disponible para la "
            + "celebración del Evento en un nuevo periodo de un año a contar desde la cancelación o "
            + "cambio de fecha del Evento. En caso de que ambas partes no logren establecer una nueva "
            + "fecha para la celebración del Evento, el importe abonado por Los NOVIOS será devuelto "
            + "íntegramente, sin perjuicio de que Los NOVIOS deberán asumir todos los gastos en que "
            + "hayan incurrido Los TOLDOS en la preparación del Evento hasta el momento en que tuviera "
            + "lugar la cancelación o cambio de fecha por las circunstancias extraordinarias antedichas.",

            "En caso de que el Evento no pudiera celebrarse o desarrollarse por causas meteorológicas, "
            + "no existirá por parte de Los TOLDOS obligación de reembolsar cantidad alguna, en tanto "
            + "Los NOVIOS declaran a la firma del presente contrato que el lugar en que se celebrará el "
            + "Evento se encuentra debidamente preparado y equipado para afrontar cualquier inclemencia "
            + "meteorológica, debiendo éste contar con un espacio alternativo interior en que pueda "
            + "desarrollarse.",

            "En caso de cancelación del Evento a instancias de Los TOLDOS, se procederá a la íntegra "
            + "devolución de cuantas cantidades hayan sido abonadas por parte de Los NOVIOS. Los TOLDOS "
            + "se comprometen a notificar la cancelación del Evento, salvo circunstancias "
            + "extraordinarias acreditadas, con un plazo de antelación mínimo de 6 meses respecto a la "
            + "fecha de celebración pactada. Igualmente, Los TOLDOS velarán, si así lo desean Los "
            + "NOVIOS, por gestionar otro grupo musical sustituto a través de su mediación en la "
            + "obtención del mejor acuerdo posible con el grupo musical alternativo.",
        ]],

        ["**Obligaciones de Los TOLDOS**", [
            "Ejecutar la actuación con los mayores estándares de diligencia profesional conforme a los "
            + "usos del sector.",

            "Personarse en el lugar del Evento dentro de los horarios previstos para el montaje, "
            + "ensayo, pruebas de sonido y actuación.",
        ]],

        ["**Obligaciones de Los NOVIOS**", [
            "Abonar los honorarios del Evento en el tiempo y forma pactados.",

            "Cumplir con los requisitos técnicos desarrollados en el Anexo I unido al presente contrato "
            + "que Los NOVIOS declaran conocer.",
        ]],

        ["**Comunicaciones entre las partes.**", [
            "Ambas partes se comprometen a mantenerse informadas de cuantas novedades, cambios y demás "
            + "circunstancias que puedan afectar al Evento sucedan, para lo que deciden establecer como "
            + "canales ordinarios de contacto los siguientes medios:",

            `Los TOLDOS: ${esc(contactEmail)}`,
            `Los NOVIOS: ${esc(contacto)}`,
        ]],
    ];
}

// =========================
// Anexo I (texto literal de la plantilla)
// =========================
const ANEXO_I = [
    "Los TOLDOS deberán disponer de al menos **2 horas** para probar el sonido y montar el "
    + "equipamiento a menos de **3 horas de la hora del Evento**, salvo excepciones previamente "
    + "acordadas con Los NOVIOS.",

    "Los TOLDOS precisarán de un espacio mínimo de **6 x 4 metros** para atender todas las "
    + "necesidades técnicas y logísticas para el desarrollo del Evento.",

    "Los TOLDOS precisarán de un acceso directo con furgoneta a una distancia máxima de **5 metros** "
    + "del espacio en que vaya a tener lugar el Evento, el cual deberá permanecer libre y despejado "
    + "durante el montaje y desmontaje, salvo excepciones previamente acordadas con Los NOVIOS.",

    "Los TOLDOS precisarán de al menos dos plazas de aparcamiento en el lugar del Evento desde el "
    + "momento del montaje hasta la finalización del Evento.",

    "Los TOLDOS precisarán de un enchufe situado a menos de **10 metros** del espacio en que vaya a "
    + "desarrollarse el Evento, con potencia suficiente para alimentar a todo el equipo.",

    "Será responsabilidad exclusiva de Los NOVIOS, quedando Los TOLDOS exentos de cualquier "
    + "responsabilidad, las contingencias que pudieran surgir en torno a:",
];

const ANEXO_I_RESPONSABILIDADES = [
    "Licencias (SGAE, AIE y derivados)",
    "Permisos.",
    "Restricciones acústicas.",
    "Denuncias por ruido.",
    "Horarios de actividad.",
];

const ANEXO_I_CONT = [
    "Los NOVIOS declaran que la finca/espacio en que se desarrollará el Evento está autorizada para "
    + "albergar actuaciones musicales, declarando igualmente que no existen restricciones "
    + "incompatibles con la actuación y Evento contratados.",

    "Los NOVIOS facultan a Los TOLDOS para acudir al Evento con fotógrafo y/o videógrafo propios "
    + "para la creación y divulgación de contenido, el cual podrá emplearse con fines promocionales. "
    + "A este respecto, Los NOVIOS podrán solicitar expresamente y por escrito a Los TOLDOS (i) que "
    + "no aparezcan invitados en el contenido divulgado y (ii) que no se identifique a ninguno de los "
    + "asistentes.",

    "Los NOVIOS reconocen que Los TOLDOS disponen de un repertorio musical propio fijo. Los NOVIOS "
    + "podrán proponer canciones o preferencias que serán valoradas por Los TOLDOS, sin que exista "
    + "obligación de atender las propuestas, toda vez que se reconoce expresamente que Los NOVIOS "
    + "contratan el estilo artístico y ambiente propio de Los TOLDOS.",

    "Los NOVIOS se comprometen a evitar que algún asistente o persona ajena a Los TOLDOS intervenga "
    + "durante el Evento o lo interrumpa, incluyendo los momentos de montaje y desmontaje. Los TOLDOS "
    + "podrán suspender o acortar el Evento en cualquier momento si existieran conductas "
    + "inapropiadas, violencia verbal o física, consignas de odio, increpaciones o invasión del "
    + "espacio de actuación por parte de invitados o cualquier persona ajena a Los TOLDOS, sin que "
    + "ello genere derecho al reembolso de los importes abonados por Los NOVIOS.",

    "Los NOVIOS responderán de cualquier daño que se produzca en los instrumentos, equipos técnicos "
    + "o humanos en cualquier momento del Evento por las conductas indicadas anteriormente por "
    + "cualquier persona ajena a Los TOLDOS.",
];

// =========================
// Tablas
// =========================
function drawClausulasHeader(cur) {
    const { doc } = cur;
    // Sin sitio para el encabezado y al menos una linea de la primera fila, la
    // cabecera "CLAUSULAS" quedaria sola al pie de la pagina.
    cur.ensure(LINE * 4 + CELL_PAD_Y * 4);

    const h = LINE + CELL_PAD_Y * 2;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(BORDER);
    doc.rect(TABLE_X, cur.y, COL_W * 2, h);

    drawRichLine(
        doc,
        wrapRich(doc, parseRich("**CLÁUSULAS**"), COL_W * 2, FS)[0],
        TABLE_X,
        cur.y + CELL_PAD_Y + LINE * 0.78,
        FS,
        { width: COL_W * 2, align: "center" }
    );

    cur.y += h;
}

/**
 * Pinta una fila de la tabla de clausulas.
 *
 * La fila se trocea si no cabe en lo que queda de pagina: "Politica de
 * cancelacion" mide por si sola casi una hoja, de modo que sin corte la
 * rejilla se saldria del papel.
 */
function drawClausulaRow(cur, label, paras) {
    const { doc } = cur;
    const innerW = COL_W - CELL_PAD_X * 2;

    const labelLines = wrapRich(doc, parseRich(label), innerW, FS);

    // Un hueco entre parrafos ocupa un renglon, que es como se ve en el .docx.
    const bodySlots = [];
    paras.forEach((p, i) => {
        const lines = wrapRich(doc, parseRich(p), innerW, FS);
        lines.forEach((line, j) => bodySlots.push({ line, last: j === lines.length - 1 }));
        if (i < paras.length - 1) bodySlots.push(null);
    });

    const totalSlots = Math.max(labelLines.length, bodySlots.length);
    let done = 0;

    while (done < totalSlots) {
        let fits = slotsThatFit(cur);
        // Arrancar una fila con una o dos lineas al pie deja una viuda fea:
        // si hay tan poco sitio, la fila entera empieza en la hoja siguiente.
        if (fits < (done === 0 ? 3 : 1)) {
            cur.newPage();
            fits = slotsThatFit(cur);
        }

        const n = Math.min(fits, totalSlots - done);
        const h = n * LINE + CELL_PAD_Y * 2;

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(BORDER);
        doc.rect(TABLE_X, cur.y, COL_W, h);
        doc.rect(TABLE_X + COL_W, cur.y, COL_W, h);

        for (let k = 0; k < n; k++) {
            const baseline = cur.y + CELL_PAD_Y + (k + 1) * LINE - LINE * 0.22;

            const lab = labelLines[done + k];
            if (lab) drawRichLine(doc, lab, TABLE_X + CELL_PAD_X, baseline, FS);

            const slot = bodySlots[done + k];
            if (slot) {
                drawRichLine(doc, slot.line, TABLE_X + COL_W + CELL_PAD_X, baseline, FS, {
                    width: innerW,
                    justify: !slot.last,
                });
            }
        }

        cur.y += h;
        done += n;
        if (done < totalSlots) cur.newPage();
    }
}

function slotsThatFit(cur) {
    const usable = PAGE_H - MARGIN_BOTTOM - cur.y - CELL_PAD_Y * 2;
    return Math.max(0, Math.floor(usable / LINE));
}

function drawFirmas(cur, { companyName, signer1, signer2 }) {
    const { doc } = cur;
    // Celdas altas: es el hueco material donde se firma a mano.
    const GAP = 26;
    cur.ensure(LINE * 2 + GAP);

    const cols = [
        { head: companyName.toUpperCase(), foot: LEGAL_REP.name },
        { head: "LOS NOVIOS", foot: `${signer1.name} y ${signer2.name}` },
    ];

    cols.forEach((col, i) => {
        const x = SIGN_X + i * SIGN_COL_W;
        const head = wrapRich(doc, parseRich(esc(col.head)), SIGN_COL_W, FS);
        const foot = wrapRich(doc, parseRich(esc(col.foot)), SIGN_COL_W, FS);

        head.forEach((line, k) => {
            drawRichLine(doc, line, x, cur.y + LINE * (k + 1), FS, { width: SIGN_COL_W, align: "center" });
        });
        foot.forEach((line, k) => {
            drawRichLine(doc, line, x, cur.y + LINE + GAP + LINE * k, FS, { width: SIGN_COL_W, align: "center" });
        });
    });

    cur.y += LINE + GAP + LINE * 2;
}

// =========================
// Motor de texto enriquecido
// =========================
// jsPDF no sabe mezclar negrita dentro de un parrafo ni justificar por su
// cuenta, y la plantilla hace ambas cosas. Estas funciones parten el texto en
// palabras con su estilo, las reparten en renglones y las pintan repartiendo
// el sobrante entre los espacios.

/** Lee las marcas `**negrita**` de la plantilla. Los datos del CRM pasan antes por esc(). */
function parseRich(md) {
    const out = [];
    for (const part of String(md ?? "").split(/(\*\*[\s\S]*?\*\*)/g)) {
        if (!part) continue;
        if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
            out.push({ text: sanitize(part.slice(2, -2)), bold: true });
        } else {
            out.push({ text: sanitize(part), bold: false });
        }
    }
    return out;
}

/** Neutraliza los asteriscos de un dato del CRM para que no se lean como negrita. */
function esc(value) {
    return String(value ?? "").replace(/\*/g, "");
}

// Las fuentes estandar de jsPDF solo alcanzan Latin-1: las comillas
// tipograficas y las rayas del .docx se caerian del PDF sin dejar rastro, asi
// que se cambian por el equivalente que si existe.
const LATIN1_FALLBACK = {
    "“": '"', "”": '"', "„": '"',
    "‘": "'", "’": "'", "‚": "'",
    "—": "-", "–": "-", "−": "-",
    "…": "...", " ": " ", " ": " ", " ": " ",
};

function sanitize(str) {
    let out = String(str ?? "").replace(
        /[–—‘’‚“”„…−   ]/g,
        (c) => LATIN1_FALLBACK[c] ?? c
    );
    if (!/[^\u0000-\u00FF]/.test(out)) return out;

    // Red de seguridad para los datos del CRM: un nombre de finca con un guion
    // largo o una letra rara no puede evaporarse de un contrato. Se intenta la
    // forma sin diacriticos y, si tampoco entra en Latin-1, se deja una marca
    // visible en vez de un hueco.
    out = out.replace(/[^\u0000-\u00FF]/g, (c) => {
        const plain = c.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        if (/^[\u0000-\u00FF]+$/.test(plain)) return plain;
        console.warn(`[Contrato] caracter no representable en el PDF: ${JSON.stringify(c)}`);
        return "?";
    });
    return out;
}

/** Reparte los segmentos en renglones que caben en `width`. */
function wrapRich(doc, segs, width, fontSize) {
    doc.setFontSize(fontSize);

    const tokens = [];
    for (const seg of segs) {
        for (const piece of seg.text.split(/(\s+)/)) {
            if (!piece) continue;
            doc.setFont("helvetica", seg.bold ? "bold" : "normal");
            tokens.push({
                text: piece,
                bold: seg.bold,
                space: /^\s+$/.test(piece),
                w: doc.getTextWidth(piece),
            });
        }
    }

    const lines = [];
    let line = [];
    let used = 0;

    const trimTail = () => {
        while (line.length && line[line.length - 1].space) used -= line.pop().w;
    };

    for (const tok of tokens) {
        if (tok.space) {
            if (!line.length) continue;          // sin sangria por espacios sueltos
            line.push(tok);
            used += tok.w;
            continue;
        }
        if (used + tok.w > width && line.length) {
            trimTail();
            lines.push(line);
            line = [];
            used = 0;
        }
        line.push(tok);
        used += tok.w;
    }
    trimTail();
    if (line.length) lines.push(line);

    return lines.length ? lines : [[]];
}

/** Pinta un renglon ya medido. `justify` estira los espacios hasta `width`. */
function drawRichLine(doc, line, x, y, fontSize, { width = 0, justify = false, align = "left" } = {}) {
    if (!line || !line.length) return;

    doc.setFontSize(fontSize);
    doc.setTextColor(0, 0, 0);

    const natural = line.reduce((sum, t) => sum + t.w, 0);
    const gaps = line.filter((t) => t.space).length;

    let extra = 0;
    let cx = x;
    if (justify && gaps && width > natural) {
        extra = (width - natural) / gaps;
    } else if (align === "center" && width) {
        cx = x + (width - natural) / 2;
    } else if (align === "right" && width) {
        cx = x + width - natural;
    }

    for (const tok of line) {
        if (tok.space) {
            cx += tok.w + extra;
            continue;
        }
        doc.setFont("helvetica", tok.bold ? "bold" : "normal");
        doc.text(tok.text, cx, y);
        cx += tok.w;
    }
}

// =========================
// Cursor de pagina
// =========================
// Lleva la posicion vertical y se encarga de que cada hoja nueva nazca ya con
// el logotipo de cabecera, como en el .docx (headerReference default y first
// apuntan al mismo logo).
function makeCursor(doc) {
    const width = doc.internal.pageSize.getWidth() - MARGIN_X * 2;

    const cur = {
        doc,
        y: CONTENT_TOP,
        width,

        newPage() {
            doc.addPage();
            drawContractLogo(doc);
            cur.y = CONTENT_TOP;
            return cur.y;
        },

        ensure(needed) {
            if (cur.y + needed > PAGE_H - MARGIN_BOTTOM) cur.newPage();
            return cur.y;
        },

        paragraph(md, { justify = true } = {}) {
            const lines = wrapRich(doc, parseRich(md), width, FS);
            lines.forEach((line, i) => {
                cur.ensure(LINE);
                drawRichLine(doc, line, MARGIN_X, cur.y + LINE * 0.78, FS, {
                    width,
                    justify: justify && i < lines.length - 1,
                });
                cur.y += LINE;
            });
            cur.y += PARA_GAP;
        },

        centered(md) {
            const lines = wrapRich(doc, parseRich(md), width, FS);
            for (const line of lines) {
                cur.ensure(LINE);
                drawRichLine(doc, line, MARGIN_X, cur.y + LINE * 0.78, FS, { width, align: "center" });
                cur.y += LINE;
            }
            cur.y += PARA_GAP;
        },

        // `marker: null` pinta un punto relleno: el bullet tipografico (U+2022)
        // no existe en Latin-1 y las fuentes estandar de jsPDF lo descartarian
        // sin avisar, dejando la vinieta en blanco.
        bullet(md, { indent = 0, marker = null } = {}) {
            const textX = MARGIN_X + indent + 4;
            const textW = width - indent - 4;
            const lines = wrapRich(doc, parseRich(md), textW, FS);

            lines.forEach((line, i) => {
                cur.ensure(LINE);
                if (i === 0) {
                    doc.setFont("helvetica", "normal");
                    doc.setFontSize(FS);
                    doc.setTextColor(0, 0, 0);
                    if (marker === null) {
                        doc.setFillColor(0, 0, 0);
                        doc.circle(MARGIN_X + indent + 0.8, cur.y + LINE * 0.45, 0.6, "F");
                    } else {
                        doc.text(marker, MARGIN_X + indent, cur.y + LINE * 0.78);
                    }
                }
                drawRichLine(doc, line, textX, cur.y + LINE * 0.78, FS, {
                    width: textW,
                    justify: i < lines.length - 1,
                });
                cur.y += LINE;
            });
            cur.y += PARA_GAP;
        },
    };

    return cur;
}
